/**
 * WithdrawAcceptanceBulkDialog
 *
 * Internal staff-only bulk action on the offer list: undoes several offers'
 * acceptances in one call via POST /offers/versions/withdraw-acceptance-bulk.
 * Before issuing any request it resolves each selected offer's currently
 * ACCEPTED version (the list payload doesn't carry version ids) and the
 * count of reservations that acceptance produced, so the confirmation states
 * the blast radius up front. The result view shows withdrawn/skipped/failed
 * as distinct counts, and each withdrawn entry expands to its released
 * reservation ids.
 *
 * client-offer-decision-persistence / Bulk withdrawal surface in the internal offer list
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import type { Offer, WithdrawAcceptanceBulkResponse } from '@ooh/contracts';
import { parseApiError } from '@ooh/contracts';
import { toast } from 'sonner';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { StatCard } from '@ooh/ui/components/stat-card';
import { CollapsibleSection } from '@ooh/ui/components/collapsible-section';
import { useWithdrawAcceptanceBulk } from '../api/mutations';
import { listVersionsOptions } from '../api/queries';
import { WithdrawReasonField } from '@ooh/screens';
import { useLockedReservationCountForOffers } from '../../../hooks/use-locked-reservation-count';
import { apiErrorToUserMessage } from '../../../lib/api-error-messages';
import type { OffersT } from '@ooh/screens';

const DIALOG_MAX_WIDTH = 'max-w-xl';

export interface WithdrawAcceptanceBulkDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-filtered to the caller's currently-selected ACCEPTED rows. */
  offers: Offer[];
  /** Of `offers`, how many the current search hides. */
  hiddenCount?: number;
}

function useAcceptedVersionIds(offers: Offer[], enabled: boolean) {
  const { auth } = useRouteContext({ from: '__root__' });
  const results = useQueries({
    queries: offers.map((offer) => ({
      ...listVersionsOptions(auth, String(offer.id)),
      enabled,
    })),
  });
  const isLoading = results.some((r) => r.isLoading);
  const byOfferId = new Map<number, number>();
  results.forEach((r, i) => {
    const accepted = r.data?.versions.find((v) => v.status === 'ACCEPTED');
    if (accepted) byOfferId.set(offers[i]!.id, accepted.id);
  });
  return { byOfferId, isLoading };
}

/** Extracted to keep WithdrawAcceptanceBulkDialog's cyclomatic complexity within the lint budget. */
function buildBulkVersionsPayload(
  offers: Offer[],
  byOfferId: Map<number, number>,
): Array<{ offerId: number; versionId: number }> {
  const entries: Array<{ offerId: number; versionId: number }> = [];
  for (const offer of offers) {
    const versionId = byOfferId.get(offer.id);
    if (versionId != null) entries.push({ offerId: offer.id, versionId });
  }
  return entries;
}

async function submitBulkWithdrawal(
  bulkMutation: ReturnType<typeof useWithdrawAcceptanceBulk>,
  versions: Array<{ offerId: number; versionId: number }>,
  reason: string,
): Promise<WithdrawAcceptanceBulkResponse | null> {
  try {
    return await bulkMutation.mutateAsync({ versions, reason: reason.trim() || undefined });
  } catch (error) {
    toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation'));
    return null;
  }
}

function countOutcomes(result: WithdrawAcceptanceBulkResponse | null) {
  return {
    withdrawn: result?.results.filter((r) => r.outcome === 'withdrawn').length ?? 0,
    skipped: result?.results.filter((r) => r.outcome === 'skipped').length ?? 0,
    failed: result?.results.filter((r) => r.outcome === 'failed').length ?? 0,
  };
}

function ResultEntry({
  entry,
  t,
}: {
  entry: WithdrawAcceptanceBulkResponse['results'][number];
  t: OffersT;
}) {
  if (entry.outcome !== 'withdrawn') {
    return (
      <div
        className="flex items-center justify-between px-4 py-2 text-sm"
        data-testid={`withdraw-bulk-entry-${entry.offerId}`}
      >
        <span>
          {t('list.bulkWithdrawAcceptance.result.offerLabel', { offerId: entry.offerId })}
        </span>
        <span className="text-xs text-muted-foreground">
          {entry.outcome === 'skipped'
            ? t('list.bulkWithdrawAcceptance.result.skippedTag')
            : t('list.bulkWithdrawAcceptance.result.failedTag', { errorCode: entry.errorCode })}
        </span>
      </div>
    );
  }
  return (
    <CollapsibleSection
      testId={`withdraw-bulk-entry-${entry.offerId}`}
      trigger={
        <span className="text-sm">
          {t('list.bulkWithdrawAcceptance.result.offerLabel', { offerId: entry.offerId })} —{' '}
          {t('list.bulkWithdrawAcceptance.result.releasedCount', {
            count: entry.reservationRelease.released.length,
          })}
        </span>
      }
    >
      <ul className="divide-y divide-border/50 px-4 py-2 text-xs text-muted-foreground">
        {entry.reservationRelease.released.map((id) => (
          <li key={id}>{t('list.bulkWithdrawAcceptance.result.reservationId', { id })}</li>
        ))}
        {entry.reservationRelease.failed.map((f) => (
          <li key={f.reservationId} className="text-destructive">
            {t('list.bulkWithdrawAcceptance.result.reservationFailed', {
              id: f.reservationId,
              errorCode: f.errorCode,
            })}
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  );
}

export function WithdrawAcceptanceBulkDialog({
  open,
  onClose,
  offers,
  hiddenCount = 0,
}: WithdrawAcceptanceBulkDialogProps) {
  const { t } = useTranslation('offers');
  const [reason, setReason] = React.useState('');
  const [result, setResult] = React.useState<WithdrawAcceptanceBulkResponse | null>(null);
  const bulkMutation = useWithdrawAcceptanceBulk();
  const { byOfferId, isLoading: versionsLoading } = useAcceptedVersionIds(offers, open);
  const { total: reservationTotal, isLoading: countsLoading } = useLockedReservationCountForOffers(
    offers.map((offer) => offer.id),
    open,
  );

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setResult(null);
      bulkMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const isLoadingPreview = versionsLoading || countsLoading;

  async function handleConfirm() {
    if (bulkMutation.isPending) return;
    const versions = buildBulkVersionsPayload(offers, byOfferId);
    if (versions.length === 0) return;
    const response = await submitBulkWithdrawal(bulkMutation, versions, reason);
    if (response) setResult(response);
  }

  const {
    withdrawn: withdrawnCount,
    skipped: skippedCount,
    failed: failedCount,
  } = countOutcomes(result);

  return (
    <ModalDialog
      onClose={onClose}
      maxWidth={DIALOG_MAX_WIDTH}
      testId="withdraw-acceptance-bulk-dialog"
      header={
        <h2 className="text-base font-semibold">{t('list.bulkWithdrawAcceptance.dialog.title')}</h2>
      }
      footer={
        result == null ? (
          <>
            <Button variant="outline" onClick={onClose}>
              {t('list.bulkWithdrawAcceptance.dialog.cancelBtn')}
            </Button>
            <Button
              disabled={bulkMutation.isPending || isLoadingPreview}
              onClick={() => void handleConfirm()}
              data-testid="withdraw-acceptance-bulk-confirm-button"
            >
              {t('list.bulkWithdrawAcceptance.dialog.confirmBtn')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>{t('list.bulkWithdrawAcceptance.result.closeBtn')}</Button>
        )
      }
    >
      {result == null ? (
        <div className="space-y-4">
          <p
            className="text-sm text-muted-foreground"
            data-testid="withdraw-acceptance-bulk-description"
          >
            {isLoadingPreview
              ? t('list.bulkWithdrawAcceptance.dialog.loadingPreview')
              : t('list.bulkWithdrawAcceptance.dialog.description', {
                  offerCount: offers.length,
                  reservationCount: reservationTotal,
                })}
          </p>
          {!isLoadingPreview && hiddenCount > 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="withdraw-acceptance-bulk-hidden-count"
            >
              {t('list.bulkWithdrawAcceptance.dialog.hiddenNotice', { count: hiddenCount })}
            </p>
          ) : null}
          <WithdrawReasonField
            id="withdraw-acceptance-bulk-reason"
            testId="withdraw-acceptance-bulk-reason-input"
            label={t('list.bulkWithdrawAcceptance.dialog.reasonLabel')}
            value={reason}
            onChange={setReason}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <StatCard
              icon={CheckCircle2}
              tone="success"
              label={t('list.bulkWithdrawAcceptance.result.withdrawnLabel')}
              value={withdrawnCount}
              loading={false}
              testId="withdraw-bulk-withdrawn-count"
            />
            <StatCard
              icon={Clock}
              tone="neutral"
              label={t('list.bulkWithdrawAcceptance.result.skippedLabel')}
              value={skippedCount}
              loading={false}
              testId="withdraw-bulk-skipped-count"
            />
            <StatCard
              icon={XCircle}
              tone="destructive"
              label={t('list.bulkWithdrawAcceptance.result.failedLabel')}
              value={failedCount}
              loading={false}
              testId="withdraw-bulk-failed-count"
            />
          </div>
          <div className="space-y-2">
            {result.results.map((entry) => (
              <ResultEntry key={entry.offerId} entry={entry} t={t} />
            ))}
          </div>
        </div>
      )}
    </ModalDialog>
  );
}
